import { DatePipe } from '@angular/common';
import { Component, HostListener, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PendoService } from 'analytics-lib';
import { INamedEntity } from 'interfaces-lib';
import { isObservable, Observable, Subscription } from 'rxjs';
import { runtimeEnvironment } from '../../../../environments/environment';
import { ISimpleTableRow } from '../../../common-components/simple-table/simple-table.interfaces';

import { IIconService } from 'interfaces-lib';
import { MessageNotificationService } from 'message-notification-service-lib';
import { UtilsService } from 'utils-service-lib';
import { ServiceProviderService } from '../../../services/service.provider.service';
import { DialogResult } from '../dialog-result';
import { EntitySelectionDialogServiceFactory } from './entity-select-dialog-service.factory';
import { IEntitySelectDialogDisplayTerms, IEntitySelectDialogService } from './ientityselectdialogservice';

export interface ISelectEntityDialogData {
  namespace: string;
  entity: INamedEntity;
  templateName: string;
  templateEntityType?: string;
}

@Component({
    selector: 'app-entity-select-dialog',
    templateUrl: './entity-select-dialog.component.html',
    styleUrls: ['../selection-dialogs.component.scss', '../selection-dialogs.chrome-extension.component.scss'],
    standalone: false
})
export class EntitySelectDialogComponent implements OnInit, OnDestroy {
  renderMode = runtimeEnvironment.renderMode;

  entities: ISimpleTableRow[] = [];
  showSpinner = false;

  columnHeadings;

  entitySelected: INamedEntity;

  displayTerms: IEntitySelectDialogDisplayTerms;

  private readonly updateUi: (update) => void;
  private readonly iconService: IIconService;
  private readonly entitySelectDialogService: IEntitySelectDialogService;
  private displayTermsSubscription?: Subscription;

  constructor(
    private utilsService: UtilsService,
    private serviceProviderService: ServiceProviderService,
    private datePipe: DatePipe,
    private ngZone: NgZone,
    private messageNotificationService: MessageNotificationService,
    private dialogRef: MatDialogRef<EntitySelectDialogComponent>,
    private pendoService: PendoService,
    entitySelectionDialogServiceFactory: EntitySelectionDialogServiceFactory,
    @Inject(MAT_DIALOG_DATA) public data: ISelectEntityDialogData
  ) {
    dialogRef.disableClose = true;
    this.updateUi = runtimeEnvironment.runningAsChromeExtension ? this.updateUiInZone : this.updateUiNoZone;
    this.iconService = serviceProviderService.getIconService(data.namespace);
    this.entitySelectDialogService = entitySelectionDialogServiceFactory.create(data.namespace);
    this.displayTerms = this.getFallbackDisplayTerms(data.entity, data.templateEntityType);
    this.setDisplayTerms(this.entitySelectDialogService.getDisplayTerms(data.entity, data.templateEntityType));
    this.columnHeadings = this.entitySelectDialogService.getDisplayColumns();
  }

  static getDefaultMatConfig(): any {
    return {
      maxWidth: runtimeEnvironment.renderMode === 'web-app' ? '88vw' : '100vw',
      minWidth: runtimeEnvironment.renderMode === 'web-app' ? '808px' : '',
      panelClass: [
        runtimeEnvironment.renderMode === 'web-app' ? '' : ['chrome-ext-full-view'],
        runtimeEnvironment.origin.namespace + '-namespace',
      ],
      height: 'auto',
      autoFocus: true,
    };
  }

  // Keyboard Shortcuts
  // - Use Escape to close the dialog
  // Keyboard Shortcut - Use Esc to clear
  @HostListener('document:keydown', ['$event'])
  protected handleKeydownEvent(event: KeyboardEvent) {
    if (!this.utilsService.escapableElementActive()) {
      if (event.code === 'Escape') {
        this.dialogRef.close();
        this.pendoService?.recordEvent('Escape key pressed - to close entity select dialog');
      }
    } else {
      this.utilsService.escapableElementActive(false);
    }
  }

  ngOnInit(): void {
    this.utilsService.dialogOpen(true);
    this.buildTable(this.data.entity);
  }

  itemRowClick(entity: INamedEntity): void {
    this.entitySelected = entity;
  }

  refreshFiles(): void {
    this.buildTable(this.data.entity);
  }

  onCancelClick(): void {
    this.dialogRef.close(DialogResult.CANCELLED);
  }

  onOkClick(): void {
    this.dialogRef.close(new DialogResult(false, [this.entitySelected]));
  }

  ngOnDestroy(): void {
    this.displayTermsSubscription?.unsubscribe();
    this.utilsService.dialogOpen(false);
  }

  private buildTable(entity: INamedEntity): void {
    this.updateUi(() => (this.showSpinner = true));

    this.entitySelectDialogService.getRows(entity, this.data.templateEntityType).subscribe(
      (tableRows) => {
        this.updateUi(() => {
          this.entities = tableRows.sort(EntitySelectDialogComponent.sortByName);
          this.showSpinner = false;
        });
      },
      (error) => {
        this.updateUi(() => (this.showSpinner = false));
        this.messageNotificationService.error('Error getting data.', error);
      }
    );
  }

  // TODO: Move to a renderUtils service?

  getIcon(type: string) {
    return this.iconService.getIcon(type);
  }

  private updateUiNoZone(update: () => void): void {
    update();
  }

  private updateUiInZone(update: () => void): void {
    this.ngZone.run(() => update());
  }

/*
We need this because Salesforce display labels are async now.
Bullhorn/Invenias return display terms synchronously from local logic.
Salesforce has to call getEntityMappings() to turn TR1__Closing_Report__c into Closing Report, so getDisplayTerms() returns an Observable.
*/
  private setDisplayTerms(displayTerms: IEntitySelectDialogDisplayTerms | Observable<IEntitySelectDialogDisplayTerms>): void {
    if (isObservable(displayTerms)) {
      this.displayTermsSubscription = displayTerms.subscribe((terms) => {
        this.updateUi(() => (this.displayTerms = terms));
      });
      return;
    }

    this.displayTerms = displayTerms;
  }

  private getFallbackDisplayTerms(entity: INamedEntity, targetEntityType?: string): IEntitySelectDialogDisplayTerms {
    const listEntityType = targetEntityType ?? entity.type;
    return {
      listEntityType,
      listEntityLabel: listEntityType,
    };
  }

  private static sortByName(a: ISimpleTableRow, b: ISimpleTableRow): number {
    return a.data.name.localeCompare(b.data.name);
  }
}
